import { type FC, useState } from "react";

import {
  Button,
  ButtonGroup,
  Column,
  Dialog,
  FormField,
  TextInput,
} from "@hightouch/htui";
import { Controller, type NestedValue, useForm } from "react-hook-form";

import * as analytics from "src/lib/analytics";
import { ConditionField } from "src/components/explore/visual/condition";
import { useFetchFilterableAudienceColumns } from "src/hooks/use-fetch-filterable-audience-columns";
import type {
  AdditionalColumn,
  EventCondition,
  EventTraitColumn,
  RelatedColumn,
  TraitType,
} from "src/types/visual";

import { TraitAggregationFields } from "./trait-aggregation-fields";
import type { MinimalParentModel } from "./types";
import { produce } from "immer";

export const AudienceEventTraitForm: FC<
  Readonly<{
    title: string;
    alias?: string;
    condition: EventCondition;
    parent: MinimalParentModel | undefined | null;
    type?: TraitType;
    config?: any;
    onClose: () => void;
    onSubmit: (value: AdditionalColumn) => Promise<void>;
  }>
> = (props) => {
  const { title, onSubmit, parent, onClose } = props;
  const relationships = parent?.relationships;
  const events = relationships?.filter(({ to_model: { event } }) =>
    Boolean(event),
  );

  const [submitting, setSubmitting] = useState(false);

  const { handleSubmit, control, watch, getValues } = useForm<{
    alias: string | undefined;
    condition: NestedValue<EventCondition>;
    type: TraitType | undefined;
    config: any;
  }>({
    defaultValues: {
      alias: props.alias,
      condition: props.condition as NestedValue<EventCondition>,
      type: props.type,
      config: props.config,
    },
  });

  // biome-ignore lint/suspicious/noTsIgnore: legacy suppression
  // @ts-ignore - no circular types until react-hook-form v8
  const type = watch("type");

  const condition = watch("condition");

  const eventModelIds = (events ?? [])
    .map((event) => event.to_model.id.toString())
    .filter(Boolean);

  // Need the filterable columns from the traits' models
  // to show the correct types for the trait options
  const { filterableColumns, isLoading: isFilterableColumnsLoading } =
    useFetchFilterableAudienceColumns(eventModelIds);

  const propertyOptions = condition.eventModelId
    ? (filterableColumns[condition.eventModelId]?.map(
        ({ column_reference, name, alias }) => ({
          value: column_reference,
          label: alias || name,
        }),
      ) ?? [])
    : [];

  const submit = async ({ alias, type, config, condition }) => {
    setSubmitting(true);

    analytics.track("Submitted Inline Audience Event Trait Form");

    const eventTraitColumn: EventTraitColumn = {
      type: "event_trait",
      filteredEvent: condition,
      traitType: type,
      traitConfig: config,
    };

    const relatedColumn: RelatedColumn = {
      type: "related",
      path: [String(condition.relationshipId)],
      column: eventTraitColumn,
    };

    await onSubmit({ alias, column: relatedColumn });

    setSubmitting(false);

    onClose();
  };

  return (
    <Dialog
      isOpen
      title={title}
      width="3xl"
      variant="form"
      actions={
        <ButtonGroup>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            isLoading={submitting}
            onClick={handleSubmit(submit)}
          >
            {title ? "Save" : "Add"}
          </Button>
        </ButtonGroup>
      }
      onClose={onClose}
    >
      <Column gap={6}>
        <Controller
          control={control}
          name="alias"
          render={({ field, fieldState: { error } }) => (
            <FormField label="Name" error={error?.message}>
              <TextInput
                ref={field.ref}
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                isInvalid={Boolean(error)}
              />
            </FormField>
          )}
        />
        <FormField label="Event">
          <Column
            gap={2}
            sx={{
              p: 4,
              fontWeight: "semibold",
              fontSize: 0,
              bg: "base.background",
              borderRadius: "6px",
              width: "fit-content",
              "& > :not(:last-child)": { mb: 4 },
            }}
          >
            <Controller
              control={control}
              name="condition"
              render={({ field }) => (
                <ConditionField
                  isEventTrait
                  audience={undefined}
                  columns={[]}
                  condition={field.value}
                  events={events}
                  parent={parent}
                  onUpdate={(updater) => {
                    field.onChange(produce(getValues("condition"), updater));
                  }}
                  onRemove={() => {}}
                />
              )}
            />
          </Column>
        </FormField>
        <TraitAggregationFields
          control={control}
          isLoading={isFilterableColumnsLoading}
          disabled={!condition.relationshipId}
          propertyOptions={propertyOptions}
          type={type as TraitType}
        />
      </Column>
    </Dialog>
  );
};
